import { Children, isValidElement, useId, type ReactNode } from "react";
import { ChevronRight, Copy, Download, Table2 } from "lucide-react";
import { TableCopyDropdown, TableDownloadDropdown } from "streamdown";
import { cn } from "@/lib/studio/utils";

/**
 * A markdown table, OPEN by default, collapsible when it is in the way.
 *
 * WHY OPEN — and this reverses an earlier call, deliberately. The table used to start closed, on
 * the reasoning that twelve rows of evidence could push the finding they support off the screen.
 * That reasoning was about a HUGE table and it made every table pay for it: a 3-row summary — the
 * common case, and usually the most information-dense line in the answer — arrived as a grey stub
 * reading "Table · 5 rows × 3 columns", which tells the reader the SHAPE of an answer while hiding
 * the answer. Requested directly from the real app: the table should be open by default.
 *
 * Nothing is lost in the other direction: the disclosure stays, so a table that IS in the way is
 * one click from gone, and the summary row still says what it holds while open.
 *
 * WHY IT REPRODUCES THE WRAPPER. Streamdown's `table` renderer delegates to an internal wrapper
 * that supplies the copy / download / fullscreen toolbar, and that wrapper is NOT exported — a
 * `components` override replaces the renderer rather than wrapping it, so the toolbar cannot be
 * inherited. The two dropdowns ARE exported and both find their table through
 * `closest('[data-streamdown="table-wrapper"]')`, so keeping that attribute keeps copy and download
 * working against the real DOM. Fullscreen is the one control not exported and not reproduced: the
 * disclosure is now the "show me all of it" affordance, so it would be a second one.
 *
 * `thead` / `tbody` / `tr` / `th` / `td` are still streamdown's own renderers — only the `table`
 * element and its container are ours, so every cell keeps its styling.
 *
 * `<details>` rather than state: it is the semantics the browser already has for a disclosure,
 * keyboard-operable and announced with no ARIA of ours, and it needs no re-render while streaming.
 */
export function CollapsibleMarkdownTable({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  const id = useId();
  const shape = describeTable(children);

  return (
    <details open className="group my-3">
      {/*
        IT HAS TO LOOK LIKE SOMETHING YOU CAN OPEN.
        
        The first version was a bare muted line with a chevron, at the same weight as the prose
        around it: "the table accordion is not catchy, it can be missed easily". A disclosure that
        reads as a sentence is a sentence — the reader has no reason to try clicking it.
        
        So it is a ROW now: its own bordered surface, full width, the label on the left and the
        verb on the right, with a hover state. Same shape as the reference cards in this
        conversation ("Changes · 1 component" / "Review changes ›"), which is the vocabulary this
        surface already uses for "there is more of this behind a click".
      */}
      {/*
        NO BOX ON THIS ROW, OPEN OR CLOSED — the table below already draws one.

        Closed, this row does the same job as the transcript's reference cards ("Changes … Review
        changes ›"): it says there is more behind a click. Those are borderless at rest, and while
        this one kept a border and a fill it was the heaviest thing in a healthy turn — a 4x3 table
        preview reading louder than the changeset opener beneath it, the hierarchy upside down.

        Open, a border here is worse, not better: `streamdownOverrides.css` gives every rendered
        table its own bordered, rounded, scrollable container, so bordering the summary too stacked
        two boxes with a seam between them — reported from the real app as "boxy UI everywhere",
        and visible as a rounded header box sitting on top of a separate rounded table box.

        So the row stays a row in both states and the table's own container is the only box. The
        hover border remains as the affordance that it is pressable.
      */}
      <summary
        className="flex cursor-pointer list-none items-center gap-2 rounded-lg border border-transparent px-3 py-2 text-[13px] text-foreground transition-colors motion-reduce:transition-none hover:border-border/60 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-controls={id}
      >
        <Table2 aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
        {/* Names what opening it costs. A bare "Table" makes the reader open it to find out. */}
        <span className="min-w-0 flex-1 truncate">{shape}</span>
        {/*
          The verb, not just an arrow. `group-open:hidden` swaps it for "Hide" so the row states
          what the NEXT click does rather than what state it is in — the reference cards read the
          same way. Both words are rendered and one is hidden, so the row's width does not jump
          between states.
        */}
        <span className="shrink-0 text-[12px] text-muted-foreground">
          <span className="group-open:hidden">Show</span>
          <span className="hidden group-open:inline">Hide</span>
        </span>
        <ChevronRight
          aria-hidden
          className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90 motion-reduce:transition-none"
        />
      </summary>

      {/*
        THE WRAPPER IS NOT A BOX EITHER — this is where the "boxy" table actually came from.

        The conversation's table CSS (`.ma-answer [data-streamdown="table"]`, multi-agent-tokens.css)
        strips the grid off the TABLE element. This div is a different thing: a rounded, bordered
        container with a filled toolbar strip welded to its top. It survived that pass, which is why
        the real app still showed a box with a grey bar across it above the rows.

        Border and fill gone; the two controls now sit quietly at the top-right of the rows they act
        on, which is where a copy control belongs. `data-streamdown="table-wrapper"` STAYS — both
        dropdowns find their table with `closest('[data-streamdown="table-wrapper"]')`, so removing
        it would silently break copy and download.
      */}
      <div id={id} data-streamdown="table-wrapper" className="relative mt-1">
        {/*
          NAMED, because they are now always on screen.

          While the table started closed these two never rendered on first paint, so nothing
          measured them. Open by default they are permanent tab stops — and streamdown ships them
          as icon-only triggers with no accessible name, which the sweep caught immediately
          ("unnamed <button> is a tab stop"). Neither component takes an `aria-label`; `children`
          is the seam they do expose, so the icon is supplied here with an `sr-only` label beside
          it. §9: every interactive element has an accessible name.
        */}
        {/*
          `.ma-answer` LIFTS THIS OUT OF THE FLOW (multi-agent-tokens.css) so the table's header sits
          flush against the card's top corners — it was this strip, above the table, that stopped it.
          The class list stays flow-friendly for every OTHER surface that renders a table, which
          keeps the toolbar as a row there.
        */}
        <div className="flex items-center justify-end gap-1">
          <TableCopyDropdown>
            <Copy aria-hidden className="h-3.5 w-3.5" />
            <span className="sr-only">Copy table</span>
          </TableCopyDropdown>
          <TableDownloadDropdown>
            <Download aria-hidden className="h-3.5 w-3.5" />
            <span className="sr-only">Download table</span>
          </TableDownloadDropdown>
        </div>
        {/*
          The scroll box is on the TABLE's container, never on the page: a wide table is the one
          place horizontal scrolling is the right answer, and §8.4 puts it here.
        */}
        {/*
          `max-h` + `overflow-auto` (not only `-x`): a long table scrolls inside its own box, which is
          what lets the header row stay sticky (`.ma-answer` pins it) instead of scrolling away with
          the page. 70vh so a table never takes more than the screen, and the reader keeps the
          conversation's context above it.
        */}
        <div className="max-h-[70vh] overflow-auto">
          <table className={cn("w-full border-collapse text-[12.5px]", className)} data-streamdown="table">
            {children}
          </table>
        </div>
      </div>
    </details>
  );
}

/**
 * "Table · 12 rows × 5 columns", from the rendered children.
 *
 * Matched on streamdown's `displayName`s rather than on tag strings, because by this point `thead`
 * and `tbody` are its memo components and `child.type` is no longer `"tbody"`. If the shape is not
 * recognisable — a different markdown pipeline, or a renamed component upstream — this degrades to
 * "Table" rather than reporting a count it could not actually take.
 */
function describeTable(children: ReactNode): string {
  const sections = Children.toArray(children).filter(isValidElement);
  const named = (name: string): ChildProps | undefined => {
    const found = sections.find((el) => (el.type as { displayName?: string })?.displayName === name);
    return found ? (found.props as ChildProps) : undefined;
  };

  const rows = countChildren(named("MarkdownTbody"));
  // The header's first row is where the columns are; `thead`'s own children are rows, not cells.
  const headRow = Children.toArray(named("MarkdownThead")?.children).filter(isValidElement)[0];
  const cols = countChildren(headRow ? (headRow.props as ChildProps) : undefined);

  if (!rows) return "Table";
  const rowLabel = `${rows} row${rows === 1 ? "" : "s"}`;
  return cols ? `Table · ${rowLabel} × ${cols} columns` : `Table · ${rowLabel}`;
}

interface ChildProps {
  children?: ReactNode;
}

/** Element children of a node's props — markdown leaves whitespace text nodes between rows. */
function countChildren(props: ChildProps | undefined): number {
  if (!props) return 0;
  return Children.toArray(props.children).filter(isValidElement).length;
}
